import _regeneratorRuntime from "C:/Users/lek-bit/Documents/jobs/2020/pannana_ml/app/pannana_ml_admin_frontend/node_modules/@babel/runtime/regenerator";
import "regenerator-runtime/runtime";
import _asyncToGenerator from "C:/Users/lek-bit/Documents/jobs/2020/pannana_ml/app/pannana_ml_admin_frontend/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";

/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Use of this source code is governed by an MIT-style
 * license that can be found in the LICENSE file or at
 * https://opensource.org/licenses/MIT.
 * =============================================================================
 */
import { dispose, mul, tensor1d, tidy } from '@tensorflow/tfjs-core';

function standardizeSampleOrClassWeights(xWeight, outputNames, weightType) {
  var numOutputs = outputNames.length;

  if (xWeight == null || Array.isArray(xWeight) && xWeight.length === 0) {
    return outputNames.map(function (name) {
      return null;
    });
  }

  if (numOutputs === 1) {
    if (Array.isArray(xWeight) && xWeight.length === 1) {
      return xWeight;
    } else if (typeof xWeight === 'object' && outputNames[0] in xWeight) {
      return [xWeight[outputNames[0]]];
    } else {
      return [xWeight];
    }
  }

  if (Array.isArray(xWeight)) {
    if (xWeight.length !== numOutputs) {
      throw new Error("Provided ".concat(weightType, " is an array of ").concat(xWeight.length, " ") + "element(s), but the model has ".concat(numOutputs, " outputs. ") + "Make sure a set of weights is provided for each model output.");
    }

    return xWeight;
  } else if (typeof xWeight === 'object' && Object.keys(xWeight).length > 0 && typeof xWeight[Object.keys(xWeight)[0]] === 'object') {
    var output = [];
    outputNames.forEach(function (outputName) {
      if (outputName in xWeight) {
        output.push(xWeight[outputName]);
      } else {
        output.push(null);
      }
    });
    return output;
  } else {
    throw new Error("The model has multiple (".concat(numOutputs, ") outputs, ") + "so ".concat(weightType, " must be either an array with ") + "".concat(numOutputs, " elements or an object with ").concat(outputNames, " keys. ") + "Provided ".concat(weightType, " not understood: ").concat(JSON.stringify(xWeight)));
  }
}
/**
 * Standardize class weighting objects.
 *
 * This function takes a single class-weighting object, an array of them,
 * or a map from output name to class-weighting object. It compares it to the
 * output name(s) of the model, base on which it outputs an array of
 * class-weighting objects of which the length matches the number of outputs.
 *
 * @param classWeight Input class-weighting object(s).
 * @param outputNames All output name(s) of the model.
 * @return An array of class-weighting objects. The length of the array matches
 *   the model's number of outputs.
 */


export function standardizeClassWeights(classWeight, outputNames) {
  return standardizeSampleOrClassWeights(classWeight, outputNames, 'classWeight');
}
export function standardizeSampleWeights(classWeight, outputNames) {
  return standardizeSampleOrClassWeights(classWeight, outputNames, 'sampleWeight');
}
/**
 * Standardize by-sample and/or by-class weights for training.
 *
 * Note that this function operates on one model output at a time. For a model
 * with multiple outputs, you must call this function multiple times.
 *
 * @param y The target tensor that the by-sample and/or by-class weight is for.
 *     The values of y are assumed to encode the classes, either directly
 *     as an integer index, or as one-hot encoding.
 * @param sampleWeight By-sample weights.
 * @param classWeight By-class weights: an object mapping class indices
 *     (integers) to a weight (float) to apply to the model's loss for the
 *     samples from this class during training. This can be useful to tell the
 *     model to "pay more attention" to samples from an under-represented class.
 * @param sampleWeightMode The mode for the sample weights.
 * @return A Promise of weight tensor, of which the size of the first dimension
 *     matches that of `y`.
 */

export function standardizeWeights(_x, _x2, _x3, _x4) {
  return _standardizeWeights.apply(this, arguments);
}
/**
 * Apply per-sample weights on the loss values from a number of samples.
 *
 * @param losses Loss tensor of shape `[batchSize]`.
 * @param sampleWeights Per-sample weight tensor of shape `[batchSize]`.
 * @returns Tensor of the same shape as`losses`.
 */

function _standardizeWeights() {
  _standardizeWeights = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(y, sampleWeight, classWeight, sampleWeightMode) {
    var yClasses, yClassIndices, classSampleWeight;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (!(sampleWeight != null || sampleWeightMode != null)) {
              _context.next = 2;
              break;
            }

            throw new Error('Support sampleWeight is not implemented yet');

          case 2:
            if (!(classWeight != null)) {
              _context.next = 15;
              break;
            }

            // Apply class weights per sample.
            yClasses = tidy(function () {
              if (y.shape.length === 1) {
                // Assume class indices.
                return y.clone();
              } else if (y.shape.length === 2) {
                if (y.shape[1] > 1) {
                  // Assume one-hot encoding of classes.
                  var axis = 1;
                  return y.argMax(axis);
                } else if (y.shape[1] === 1) {
                  // Class index.
                  return y.reshape([y.shape[0]]);
                } else {
                  throw new Error("Encountered unexpected last-dimension size (".concat(y.shape[1], ") ") + "during handling of class weights. The size is expected to be " + ">= 1.");
                }
              } else {
                throw new Error("Unexpected rank of target (y) tensor (".concat(y.rank, ") during ") + "handling of class weights. The rank is expected to be 1 or 2.");
              }
            });
            _context.t0 = Array;
            _context.next = 7;
            return yClasses.data();

          case 7:
            _context.t1 = _context.sent;
            yClassIndices = _context.t0.from.call(_context.t0, _context.t1);
            dispose(yClasses);
            classSampleWeight = [];
            yClassIndices.forEach(function (classIndex) {
              if (classWeight[classIndex] == null) {
                throw new Error("classWeight must contain all classes in the training data. " + "The class ".concat(classIndex, " exists in the data but not in ") + "classWeight");
              } else {
                classSampleWeight.push(classWeight[classIndex]);
              }
            });
            return _context.abrupt("return", tensor1d(classSampleWeight, 'float32'));

          case 15:
            return _context.abrupt("return", null);

          case 16:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _standardizeWeights.apply(this, arguments);
}

export function computeWeightedLoss(losses, sampleWeights) {
  return mul(losses, sampleWeights);
}